import { useState } from "react";
import { PricingModal } from "@/ui-components/PricingModal";
import { getTrialCtaLabel } from "@/utils/currency";
import { trackTrialCtaClick } from "@/utils/gtm";
import { useDemoGuideStore } from "./useDemoGuideStore";

/**
 * The ask, shown when the run completes.
 *
 * This is the only hard CTA in the demo. The widget headers used to carry one
 * each, which made the product look restricted rather than premium and asked
 * for money from people who hadn't yet seen anything worth paying for. Here
 * they have just built a desk, filtered it, put it on a second screen and
 * watched a watchlist assemble itself — so the ask lands against something
 * concrete rather than against the chrome.
 */
export default function FlowCompleteCta({
  completion,
}: {
  completion: { headline: string; body: string };
}) {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { assetClass, templateId, exitRun } = useDemoGuideStore((s) => ({
    assetClass: s.state?.assetClass,
    templateId: s.state?.templateId,
    exitRun: s.exitRun,
  }));

  return (
    <>
      <PricingModal isOpen={pricingOpen} onClose={() => setPricingOpen(false)} />

      <p className="text-2xl font-semibold leading-snug text-gray-100 short-screen:text-xl">
        {completion.headline}
      </p>
      <p className="mt-2 text-base leading-relaxed text-gray-300">
        {completion.body}
      </p>

      <button
        type="button"
        onClick={() => {
          trackTrialCtaClick({
            surface: "demo_guide",
            ...(templateId && { flow: templateId }),
            ...(assetClass && { asset_class: assetClass }),
          });
          setPricingOpen(true);
        }}
        data-analytics-id="demo-guide-trial-cta"
        // Solid fill, white text. The tinted-outline treatment the rest of
        // the flow uses is right for "next step" buttons and wrong for the
        // one commercial action on the screen — squawk-blue text on a 15%
        // squawk-blue wash was the lowest-contrast thing in the panel.
        className="mt-auto w-full cursor-pointer rounded bg-squawk-blue px-4 py-3.5 text-base font-semibold text-white transition-colors hover:bg-squawk-blue/85"
      >
        {getTrialCtaLabel()}
      </button>

      <button
        type="button"
        onClick={exitRun}
        className="mt-3 w-full cursor-pointer bg-transparent text-sm text-gray-400 transition-colors hover:text-gray-200"
        data-analytics-id="demo-guide-keep-exploring"
      >
        Keep exploring
      </button>
    </>
  );
}
